import { callUnaryMethod, useTransport } from "@connectrpc/connect-query";
import { useQuery } from "@tanstack/react-query";
import { ReservationService, ReservationStatus } from "@viajero/proto/guest/v1";
import { getReservationToken } from "@/lib/reservationToken";

// 完了画面のポーリング間隔 (§3.1)。決済完了 Webhook の到達までは数秒間隔で確定を確認する。
const POLL_INTERVAL_MS = 3_000;

/**
 * ReservationService.GetReservation を connect-query の transport でラップするフック。
 *
 * 予約完了画面 (/reservations/{予約トークン}/complete) で予約詳細を取得する。`x-reservation-token`
 * ヘッダは lib/client.ts の reservationTokenInterceptor が付与するため、呼び出し側はリクエスト
 * パラメータを意識しなくてよい (§6.5)。ただしリクエストが空メッセージのため、connect-query 標準の
 * useQuery だと queryKey がリクエスト内容だけで決まり予約トークンを区別できない。予約トークンごとに
 * キャッシュを分離するため queryKey に予約トークンを含める (useGetReservationContext.ts と同方式)。
 *
 * success_url 到達時点では決済完了 Webhook が未着で status=pending_payment のことがあるため、
 * 確定 (confirmed) または終端状態に達するまで数秒間隔でポーリングする (§3.1)。
 */
export function useGetReservation() {
  const transport = useTransport();
  const reservationToken = getReservationToken();
  return useQuery({
    queryKey: ["reservation", reservationToken],
    queryFn: ({ signal }) =>
      callUnaryMethod(transport, ReservationService.method.getReservation, {}, { signal }),
    refetchInterval: (query) => {
      // 一度も取得できていない恒久エラー (無効トークン等) の場合のみ停止する。既に data がある場合は
      // 一時的な通信エラー (瞬断等) でも status 判定に任せてポーリングを継続し、復帰できるようにする。
      if (query.state.error && query.state.data === undefined) {
        return false;
      }
      const status = query.state.data?.reservation?.status;
      // 決済待ち (未着含む) の間はポーリング継続。確定・失敗・キャンセル等の確定状態で停止する。
      if (
        status === undefined ||
        status === ReservationStatus.UNSPECIFIED ||
        status === ReservationStatus.PENDING_PAYMENT
      ) {
        return POLL_INTERVAL_MS;
      }
      return false;
    },
  });
}
